import React, { useState, useEffect } from 'react';

import { TreeItem2 as MUITreeItem } from '@mui/x-tree-view';
import classNames from 'classnames';

import { ColoredChip } from '../ColoredChip';
import { Icon } from '../Icon';
import { EllipsisText } from '../EllipsisText';
import { Box } from '../Box';
import { CircularProgress } from '../CircularProgress';
import { Callout } from '../Callout';
import { Checkbox } from '../Checkbox';

import { TreeViewItem as Item } from './Item.interface';
import { useStyles } from './TreeItem.styles';

interface BaseTreeItemProps {
  item: Item;
  showRulers?: boolean;
  showCounters?: boolean;
  expanded?: string[];
  onSelect: (value: string, label?: string, item?: Item) => void;
  onExpand: (value: string) => void;
  onChildrenLoad?: (unitId: string) => Promise<Item[]>;
  renderOption?: (item: Item) => React.ReactNode;
}

interface SingleSelectTreeItemProps extends BaseTreeItemProps {
  multiple?: false;
  selected: string;
}
interface MultiSelectTreeItemProps extends BaseTreeItemProps {
  multiple: true;
  selected: string[];
}

export type TreeItemProps =
  | SingleSelectTreeItemProps
  | MultiSelectTreeItemProps;

/**
 * Компонент элемента дерева, который может быть развернут и выбран, поддерживающий как одиночный, так и множественный выбор.
 * Он также может асинхронно загружать дочерние элементы и обрабатывать состояния загрузки и ошибок.
 *
 * @param {TreeItemProps} props - Свойства для компонента TreeItem.
 * @param {Item} props.item - Объект данных, представляющий элемент дерева.
 * @param {boolean} [props.showRulers=true] - Нужно ли отображать линии для элемента дерева.
 * @param {boolean} [props.showCounters=true] - Нужно ли отображать счётчики, связанные с элементом дерева.
 * @param {string[]} [props.expanded=[]] - Массив идентификаторов узлов, которые в настоящее время развернуты.
 * @param {function} props.onSelect - Колбэк-функция, вызываемая при клике на метку элемента дерева.
 *                                     Принимает два аргумента: идентификатор узла и метку узла.
 * @param {function} props.onExpand - Колбэк-функция, вызываемая при клике на иконку элемента дерева,
 *                                     используется для разворачивания или сворачивания элемента. Принимает один аргумент: идентификатор узла.
 * @param {function} [props.onChildrenLoad] - Асинхронная функция для загрузки дочерних элементов элемента дерева.
 *                                             Принимает один аргумент: идентификатор юнита и возвращает промис,
 *                                             который разрешается в массив элементов.
 * @param {boolean} [props.multiple=false] - Указывает, поддерживает ли дерево множественный выбор.
 * @param {string | string[]} props.selected - Идентификатор текущего выбранного узла для одиночного выбора или массив
 *                                             идентификаторов выбранных узлов для множественного выбора.
 * @returns {JSX.Element} - Отрисованный компонент TreeItem.
 */

export const TreeItem = (props: TreeItemProps) => {
  const {
    item,
    showRulers = true,
    showCounters = true,
    expanded,
    onSelect,
    onExpand,
    onChildrenLoad,
    renderOption,
  } = props;

  const [loadedChildren, setLoadedChildren] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const isExpanded = expanded?.some(
    (expandedItems) => expandedItems === item.id,
  );

  const { classes: rawClasses } = useStyles();
  const { itemLabel, itemCounter, groupTransition, label, ...classes } =
    rawClasses;

  useEffect(() => {
    if (
      isExpanded &&
      item.hasChildrenToLoad &&
      !item.children?.length &&
      !loadedChildren.length
    ) {
      setIsLoading(true);
      setIsError(false);
      onChildrenLoad?.(item.id)
        .then((result) => {
          setIsLoading(false);
          setLoadedChildren(result);
        })
        .catch(() => {
          setIsLoading(false);
          setIsError(true);
          setLoadedChildren([]);
        });
    }
  }, [
    isExpanded,
    item.children?.length,
    item.hasChildrenToLoad,
    item.id,
    loadedChildren.length,
    onChildrenLoad,
    item,
  ]);

  /**
   * Обработчик события клика на метку элемента дерева.
   * Предотвращает распространение события и вызывает колбэк onSelect с идентификатором и меткой узла.
   *
   * @param {React.MouseEvent<Element, MouseEvent>} event - Событие клика на метке элемента дерева.
   */
  const handleLabelClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) return;
    onSelect(item.id, item.label, item);
  };

  /**
   * Обработчик события клика на иконку элемента дерева.
   * Предотвращает распространение события и вызывает колбэк onExpand с идентификатором узла.
   *
   * @param {React.MouseEvent<Element, MouseEvent>} event - Событие клика на иконку элемента дерева.
   */
  const handleIconClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    onExpand(item.id);
  };

  /**
   * Определяет, является ли состояние чекбокса индетерминированным.
   * Индетерминированное состояние возникает, если часть дочерних узлов выбрана, а другая нет.
   * Если все дочерние узлы выбраны, проверяет состояние их дочерних узлов рекурсивно.
   *
   * @param {string[]} value - Массив идентификаторов выбранных узлов.
   * @param {Item} option - Элемент дерева, для которого проверяется состояние.
   * @returns {boolean} - Флаг, указывающий, является ли состояние чекбокса индетерминированным.
   */
  const getIsIndeterminate = (value: string[], option: Item): boolean => {
    if (value.includes(option.id)) {
      if (option.children) {
        if (!option.children.every((child) => value.includes(child.id))) {
          return true;
        } else {
          return option.children.every((child) =>
            getIsIndeterminate(value, child),
          );
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const childrenToShow =
    item?.children && !!item.children.length ? item.children : loadedChildren;
  const isShowChildren = !!childrenToShow.length || item.hasChildrenToLoad;

  const labelContent = renderOption ? (
    renderOption(item)
  ) : (
    <EllipsisText wordBreak="break-all">{item.label}</EllipsisText>
  );

  return (
    <MUITreeItem
      itemId={item.id}
      classes={{
        ...classes,
        ...(showRulers ? { groupTransition } : {}),
        label: classNames(label, 'aqa_tree_item'),
      }}
      slotProps={{
        label: { onClick: (event) => handleLabelClick(event) },
        iconContainer: { onClick: (event) => handleIconClick(event) },
      }}
      data-testid="tree-item-root-test-id"
      label={
        <div
          className={
            item.disabled ? rawClasses.itemDisabled : rawClasses.itemLabel
          }
        >
          {props.multiple ? (
            <Box display="flex" alignItems="center" gap="8">
              <Checkbox
                checked={props.selected.includes(item.id)}
                indeterminate={getIsIndeterminate(props.selected, item)}
                disabled={item.disabled}
              />
              {labelContent}
            </Box>
          ) : (
            labelContent
          )}
          <Box display="flex" gap="8">
            {item.isSeparate && !item.hasSeparateAbove && (
              <ColoredChip
                startIcon={
                  <Icon
                    name="vkhrtek_separate_unit"
                    color="text.light.tertirary"
                    size="12"
                  />
                }
                size="extraSmall"
                color="darkgray"
              />
            )}
            {showCounters && Number.isInteger(item.count) && (
              <div className={itemCounter}>{item.count}</div>
            )}
          </Box>
        </div>
      }
    >
      {isShowChildren && (
        <>
          {childrenToShow &&
            childrenToShow.map((child) => (
              <TreeItem
                key={child.id}
                item={child}
                showRulers={showRulers}
                showCounters={showCounters}
                onSelect={onSelect}
                onExpand={onExpand}
                onChildrenLoad={onChildrenLoad}
                expanded={expanded}
                renderOption={renderOption}
                {...(props.multiple
                  ? {
                      multiple: props.multiple,
                      selected: props.selected,
                    }
                  : { selected: props.selected })}
              />
            ))}
          {isLoading && (
            <Box
              display="flex"
              justifyContent="center"
              py="16"
              data-testid="circular-progress-loader-test-id"
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {isError && (
            <Box py="24" px="16">
              <Callout variant="error" showIcon>
                Не удалось загрузить оргструктуру. Обновите страницу и
                попробуйте еще раз
              </Callout>
            </Box>
          )}
        </>
      )}
    </MUITreeItem>
  );
};
